import React from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useCustomerOrderDetail } from '../hooks/useQueries';
import './CustomerOrderDetail.css';

const norm = (s) => (s ?? '').toString().trim().toLowerCase();

// Same logic as Account.jsx's order history list — order.js returns the full
// product_images array (tagged with variant_id and custom_option_id),
// product_variants, and customization_fields (with their options) on each
// item's product, so we can resolve both the purchased color AND the
// purchased customization option without an extra product fetch.
//
// item.variants                      → snapshot of purchased variant(s), e.g. "Green"
// item.customizations                 → snapshot of purchased customization(s), e.g. field_label/field_value
// item.product.product_variants       → current variants for that product, gives us the id
// item.product.customization_fields   → current fields+options, gives us the option id
// item.product.product_images         → images, each tagged with variant_id / custom_option_id
const resolveOrderItemImage = (item) => {
  const images = item.product?.product_images ?? [];
  const primary = images.find((img) => img.is_primary) ?? images[0];
  const fallback = primary?.image_url ?? null;

  // Match the purchased color, if any
  const colorVariant = (item.variants || []).find(
    (v) => norm(v.variant_type_name) === 'color'
  );
  const matchedVariant = colorVariant
    ? (item.product?.product_variants || []).find(
        (v) =>
          norm(v.variant_name) === norm(colorVariant.variant_name) ||
          norm(v.variant_value) === norm(colorVariant.variant_value)
      )
    : null;

  // Match any purchased select/radio customization to its option id
  const productFields = item.product?.customization_fields ?? [];
  let matchedOptionId = null;
  (item.customizations || []).forEach((c) => {
    const field = productFields.find((f) => norm(f.field_label) === norm(c.field_label));
    const opt = (field?.customization_field_options || []).find(
      (o) => norm(o.option_value) === norm(c.field_value) || norm(o.option_label) === norm(c.field_value)
    );
    if (opt) matchedOptionId = opt.id;
  });

  // Prefer an image tagged with both the matched color and the matched option
  if (matchedVariant && matchedOptionId) {
    const both = images.find(
      (img) => img.variant_id === matchedVariant.id && img.custom_option_id === matchedOptionId
    );
    if (both) return both.image_url;
  }
  if (matchedVariant) {
    const colorImg = images.find((img) => img.variant_id === matchedVariant.id && !img.custom_option_id);
    if (colorImg) return colorImg.image_url;
  }
  if (matchedOptionId) {
    const optionImg = images.find((img) => img.custom_option_id === matchedOptionId && !img.variant_id);
    if (optionImg) return optionImg.image_url;
  }
  return fallback;
};

/* ─── Status config ───────────────────────────────────────── */
const STATUS_STEP = {
  pending:   0,
  confirmed: 1,
  processing:   2,
  completed: 3,
  delivered: 4,
  cancelled: -1,
};

const STATUS_NOTE = {
  pending:   'Your order has been placed and is awaiting confirmation.',
  confirmed: 'Your order has been confirmed and is being prepared.',
  processing:   'Your order is on its way!',
  completed: 'Your order has been completed. Enjoy!',
  delivered: 'Your order has been delivered. We hope you loved it!',
  cancelled: 'This order has been cancelled.',
};

const STATUS_NOTE_COLOR = {
  pending:   '#c2410c',
  confirmed: '#1d4ed8',
  processing:   '#7c3aed',
  completed: '#15803d',
  delivered: '#047857',
  cancelled: '#b91c1c',
};

const STEPS = ['Order placed', 'Confirmed', 'Processing', 'Completed', 'Delivered'];

/* ─── Progress Bar ────────────────────────────────────────── */
function OrderProgress({ status }) {
  const currentStep = STATUS_STEP[status] ?? 0;
  const isCancelled = status === 'cancelled';
  const pct = isCancelled ? 0 : (currentStep / (STEPS.length - 1)) * 100;

  return (
    <div className="cod-progress-wrap">
      {STATUS_NOTE[status] && (
        <p className="cod-progress-note" style={{ color: STATUS_NOTE_COLOR[status] ?? '#333' }}>
          {STATUS_NOTE[status]}
        </p>
      )}
      <div className="cod-track">
        <div className="cod-track-bg" />
        <div className="cod-track-fill" style={{ width: `${pct}%` }} />
      </div>
      <div className="cod-steps">
        {STEPS.map((step, i) => (
          <span
            key={step}
            className={`cod-step ${i <= currentStep && !isCancelled ? 'cod-step--done' : ''} ${i === currentStep && !isCancelled ? 'cod-step--active' : ''}`}
          >
            {step}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ─── Item Card ───────────────────────────────────────────── */
function OrderItemCard({ item, address, customer, status }) {
  const productTitle = item.product?.title ?? `Product #${item.product_id}`;
  const imageUrl     = resolveOrderItemImage(item);
  const variants     = item.variants ?? [];
  const addons       = item.addons ?? [];
  const customizations = item.customizations ?? [];

  const fullName = customer
    ? `${customer.first_name ?? ''} ${customer.last_name ?? ''}`.trim()
    : '—';

  return (
    <div className="cod-item-card">
      <div className="cod-item-main">

        {/* Image */}
        <div className="cod-item-img-wrap">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={productTitle}
              className="cod-item-img"
              onError={e => { e.target.style.display = 'none'; }}
            />
          ) : (
            <div style={{
              width: '100%', height: '100%',
              background: 'var(--bg-warm)',
              display: 'flex', alignItems: 'center',
              justifyContent: 'center', fontSize: 36,
            }}>🎁</div>
          )}
        </div>

        {/* Name + details */}
        <div className="cod-item-info">
          <p className="cod-item-name">{productTitle}</p>

          {variants.length > 0 && (
            <p className="cod-item-variant">
              {variants.map(v => {
                const label = v.variant_name || v.variant_value;
                const adj = Number(v.price_adjustment || 0);
                return adj > 0 ? `${label} (+₹${adj.toLocaleString('en-IN')})` : label;
              }).filter(Boolean).join(', ')}
            </p>
          )}

          {customizations.length > 0 && (
            <div style={{ marginTop: 6 }}>
              {customizations.map(c => (
                <p key={c.id} className="cod-item-variant">
                  {c.field_label}: <strong>{c.field_value}</strong>
                  {Number(c.price) > 0 && ` (+₹${Number(c.price).toLocaleString('en-IN')})`}
                </p>
              ))}
            </div>
          )}

          <div className='item-meta'>
            <p className="cod-item-qty">₹{item.base_price} × {item.quantity}</p>
            <p className="cod-item-qty">₹{item.base_price * item.quantity}</p>
          </div>

          {Number(item.variant_price) > 0 && (
            <div className='item-meta'>
              <p className="cod-item-variant">Variant</p>
              <p className="cod-item-qty">₹{Number(item.variant_price).toLocaleString('en-IN')}</p>
            </div>
          )}

          {Number(item.customization_price) > 0 && (
            <div className='item-meta'>
              <p className="cod-item-variant">Customization</p>
              <p className="cod-item-qty">₹{Number(item.customization_price).toLocaleString('en-IN')}</p>
            </div>
          )}

          {addons.length > 0 && (
            <div style={{ marginTop: 6 }}>
              {addons.map(a => (
                <div className='item-meta'>
                <p key={a.id} className="cod-item-variant">
                  🧩 {a.addon_name} (+₹{Number(a.price).toLocaleString('en-IN')})
                  × {item.quantity}
                </p>
                <p className="cod-item-qty">₹{Number(a.price) * item.quantity}</p>
                </div>
              ))}
            </div>
          )}

          <div className='item-meta'>
            <p className="cod-item-qty">Item Total</p>
            <p className="cod-item-qty">₹{Number(item.total_price).toLocaleString('en-IN')}</p>
          </div>
        </div>

        {/* Delivery address */}
        {address && (
          <div className="cod-item-col">
            <p className="cod-col-title">Delivery address</p>
            <p className="cod-col-name">{fullName}</p>
            <p className="cod-col-text">{address.address_line}</p>
            <p className="cod-col-text">{address.city}, {address.state}</p>
            <p className="cod-col-text">{address.pincode}</p>
          </div>
        )}

        {/* Contact info */}
        {customer && (
          <div className="cod-item-col">
            <p className="cod-col-title">Contact</p>
            <p className="cod-col-text">{customer.email}</p>
            <p className="cod-col-text">{customer.phone}</p>
          </div>
        )}
      </div>

      <OrderProgress status={status} />
    </div>
  );
}

/* ─── Skeleton ────────────────────────────────────────────── */
function DetailSkeleton() {
  const bar = (w, h = 14) => ({
    background: 'linear-gradient(90deg,#f0f0f0 25%,#e8e8e8 50%,#f0f0f0 75%)',
    backgroundSize: '800px 100%',
    borderRadius: 6, height: h, width: w, marginBottom: 10,
  });
  return (
    <div className="cod-container" style={{ paddingTop: 40 }}>
      <div style={bar('40%', 28)} />
      <div style={bar('20%', 14)} />
      <div style={{ border: '1.5px solid var(--border)', borderRadius: 16, overflow: 'hidden', marginTop: 24 }}>
        <div style={{ padding: 28 }}>
          <div style={{ display: 'flex', gap: 20 }}>
            <div style={{ width: 120, height: 128, borderRadius: 10, background: '#f0ebe4', flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={bar('70%', 16)} />
              <div style={bar('40%', 12)} />
              <div style={bar('25%', 12)} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Main Page ───────────────────────────────────────────── */
const CustomerOrderDetail = () => {
  const { id }   = useParams();
  const navigate = useNavigate();

  // Get customer id from localStorage
  const storedCustomer = (() => {
    try { return JSON.parse(localStorage.getItem('customer')); } catch { return null; }
  })();
  const customerId = storedCustomer?.id ?? null;

  const { data: resp, isLoading, isError } = useCustomerOrderDetail(customerId, id);

  // Your API wraps response as { status, message, data: <order> }
  const order = resp?.data ?? null;

  if (!customerId) {
    navigate('/login', { replace: true });
    return null;
  }

  if (isLoading) {
    return (
      <div className="cod-page">
        <div className="breadcrumb-bar">
          <div className="container">
            <div className="breadcrumb">
              <Link to="/">Home</Link><span>›</span>
              <Link to="/account">My Account</Link><span>›</span>
              <span>Order #{id}</span>
            </div>
          </div>
        </div>
        <div className="container">
          <DetailSkeleton />
        </div>
      </div>
    );
  }

  if (isError || !order) {
    return (
      <div className="cod-page">
        <div className="breadcrumb-bar">
          <div className="container">
            <div className="breadcrumb">
              <Link to="/">Home</Link><span>›</span>
              <Link to="/account">My Account</Link><span>›</span>
              <span>Order #{id}</span>
            </div>
          </div>
        </div>
        <div className="container" style={{ textAlign: 'center', padding: '80px 24px' }}>
          <div style={{ fontSize: 60, marginBottom: 20 }}>📦</div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', marginBottom: 12 }}>
            Order not found
          </h2>
          <p style={{ color: 'var(--text-muted)', marginBottom: 24 }}>
            We couldn't find this order. It may belong to a different account.
          </p>
          <Link to="/account" className="btn btn-primary-warm">Back to Account</Link>
        </div>
      </div>
    );
  }

  /* ── Derived values ── */
  const customer  = order.customer  ?? null;
  const address   = order.address   ?? null;
  const items     = order.items     ?? [];
  const fullName  = customer
    ? `${customer.first_name ?? ''} ${customer.last_name ?? ''}`.trim()
    : '—';
  const placedDate = new Date(order.createdAt).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'long', year: 'numeric',
  });

  const subtotal       = Number(order.subtotal       ?? 0);
  const deliveryCharge = Number(order.delivery_charge ?? 0);
  const total          = Number(order.total_amount   ?? 0);
  const payments       = order.payments ?? [];

  return (
    <div className="cod-page">
      {/* Breadcrumb */}
      <div className="breadcrumb-bar">
        <div className="container">
          <div className="breadcrumb">
            <Link to="/">Home</Link><span>›</span>
            <Link to="/account" state={{ tab: 'Orders history' }}>My Account</Link><span>›</span>
            <span>Order #{order.id}</span>
          </div>
        </div>
      </div>

      <div className="container cod-container">

        {/* ── Account header ── */}
        <div className="acc-page-header">
          <h1 className="acc-page-title">Account</h1>
          <p className="acc-page-user">
            <strong>{fullName},</strong> {customer?.email ?? ''}
          </p>
        </div>

        {/* ── Tabs ── */}
        <div className="acc-tabs">
          {['Settings', 'Orders history'].map(tab => (
            <button
              key={tab}
              className={`acc-tab ${tab === 'Orders history' ? 'acc-tab--active' : ''}`}
              onClick={() => navigate('/account', { state: { tab } })}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* ── Order header ── */}
        <div className="cod-order-header">
          <div>
            <p className="cod-placed-label">
              Order placed <strong>{placedDate}</strong>
            </p>
            <h2 className="cod-order-title">Order #{order.id}</h2>
          </div>
        </div>

        {/* ── Item Cards ── */}
        <div className="cod-items-list">
          {items.map(item => (
            <OrderItemCard
              key={item.id}
              item={item}
              address={address}
              customer={customer}
              status={order.status}
            />
          ))}
        </div>

        {/* ── Summary grid ── */}
        <div className="cod-summary-grid">

          {/* Billing address */}
          <div className="cod-summary-col">
            <p className="cod-summary-col-title">Billing address</p>
            <p className="cod-summary-val cod-summary-val--name">{fullName}</p>
            {address && (
              <>
                <p className="cod-summary-val">{address.address_line}</p>
                <p className="cod-summary-val">{address.city}, {address.state}</p>
                <p className="cod-summary-val">{address.pincode}</p>
              </>
            )}
          </div>

          {/* Contact / Payment info */}
          <div className="cod-summary-col">
            <p className="cod-summary-col-title">Contact information</p>
            {customer && (
              <>
                <p className="cod-summary-val cod-summary-val--name">{fullName}</p>
                <p className="cod-summary-val">{customer.email}</p>
                <p className="cod-summary-val">{customer.phone}</p>
              </>
            )}
          </div>

          {/* Order totals */}
          <div className="cod-totals">
            <div className="cod-total-row">
              <span>Subtotal</span>
              <span>₹{subtotal.toLocaleString('en-IN')}</span>
            </div>
            <div className="cod-total-row">
              <span>Delivery</span>
              <span>
                {deliveryCharge === 0
                  ? <span className="cod-free">FREE</span>
                  : `₹${deliveryCharge.toLocaleString('en-IN')}`}
              </span>
            </div>
            <div className="cod-total-row cod-total-row--grand">
              <span>Order total</span>
              <span>₹{total.toLocaleString('en-IN')}</span>
            </div>
          </div>
        </div>

        {/* ── Payment Details ── */}
        {payments.length > 0 && (
          <div style={{
            marginTop: 32,
            border: '1.5px solid var(--border)',
            borderRadius: 16,
            overflow: 'hidden',
          }}>
            {/* Header */}
            <div style={{
              padding: '16px 24px',
              background: 'var(--bg-warm)',
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}>
              <span style={{ fontSize: 18 }}>💳</span>
              <p style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)', margin: 0 }}>
                Payment Details
              </p>
            </div>

            {/* Payment rows */}
            {payments.map((payment, i) => {
              const isPaid = payment.status === 'paid';
              const paymentDate = new Date(payment.createdAt).toLocaleDateString('en-IN', {
                day: 'numeric', month: 'long', year: 'numeric',
              });
              const paymentTime = new Date(payment.createdAt).toLocaleTimeString('en-IN', {
                hour: '2-digit', minute: '2-digit',
              });

              return (
                <div
                  key={payment.id ?? i}
                  style={{
                    padding: '20px 24px',
                    borderBottom: i < payments.length - 1 ? '1px solid var(--border)' : 'none',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                    gap: 20,
                    alignItems: 'start',
                  }}
                >
                  {/* Amount */}
                  <div>
                    <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
                      Amount Paid
                    </p>
                    <p style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)', margin: 0 }}>
                      ₹{Number(payment.amount).toLocaleString('en-IN')}
                    </p>
                  </div>

                  {/* Method */}
                  <div>
                    <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
                      Payment Method
                    </p>
                    <p style={{
                      fontSize: 13, fontWeight: 600, color: 'var(--text)',
                      textTransform: 'capitalize', margin: 0,
                      display: 'flex', alignItems: 'center', gap: 6,
                    }}>
                      {payment.payment_method === 'card' && '💳 '}
                      {payment.payment_method === 'upi'  && '📱 '}
                      {payment.payment_method === 'cod'  && '💵 '}
                      {!['card','upi','cod'].includes(payment.payment_method) && '🏦'}
                      {payment.payment_method}
                    </p>
                  </div>

                  {/* Status */}
                  <div>
                    <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
                      Status
                    </p>
                    <span style={{
                      display: 'inline-block',
                      padding: '3px 12px',
                      borderRadius: 20,
                      fontSize: 12,
                      fontWeight: 700,
                      textTransform: 'capitalize',
                      background: isPaid ? '#f0fdf4' : '#fef2f2',
                      color:      isPaid ? '#15803d' : '#b91c1c',
                      border:     `1px solid ${isPaid ? '#bbf7d0' : '#fecaca'}`,
                    }}>
                      {payment.status}
                    </span>
                  </div>

                  {/* Payment ID */}
                  {payment.razorpay_payment_id && (
                    <div>
                      <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
                        Payment ID
                      </p>
                      <p style={{
                        fontSize: 12, fontFamily: 'monospace',
                        color: 'var(--text-muted)', margin: 0,
                        background: 'var(--bg-warm)',
                        padding: '4px 8px', borderRadius: 6,
                        border: '1px solid var(--border)',
                        display: 'inline-block',
                      }}>
                        {payment.razorpay_payment_id}
                      </p>
                    </div>
                  )}

                  {/* Date */}
                  <div>
                    <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
                      Paid On
                    </p>
                    <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>
                      {paymentDate}
                    </p>
                    <p style={{ fontSize: 12, color: 'var(--text-light)', margin: '2px 0 0' }}>
                      {paymentTime}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Back button ── */}
        <div className="cod-back-wrap">
          <Link
            to="/account"
            state={{ tab: 'Orders history' }}
            className="cod-back-link"
          >
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            Back to Orders
          </Link>
        </div>

      </div>
    </div>
  );
};

export default CustomerOrderDetail;